import { createContext, useContext, type ReactNode } from "react";
import {
  useNotifications,
  type AppNotification,
} from "./hooks/useNotifications";

import { useNotificationSound } from "./hooks/useNotificationSound";

/**
 * Shared notifications state for the seller portal. Opening the authenticated
 * feed (one live server query, limit 50) is expensive, so exactly ONE subscription is
 * mounted here — high in the tree — and both the desktop `NotificationBell`
 * (AppShell top bar) and the `MobileHeaderNotificationBell` (rendered by every
 * `MobileScreenHeader`, which mounts its action row twice) read from it via
 * `useNotificationsContext`. This replaces the former per-bell `useNotifications`
 * call that opened two listeners per mobile screen.
 */
type NotificationsContextValue = {
  soundStatus: ReturnType<typeof useNotificationSound>["soundStatus"];
  enableSound: () => void | Promise<void>;
  muteSound: () => void;
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

export function NotificationsProvider({
  sellerId,
  storeId,
  children,
}: {
  sellerId: string | undefined;
  storeId: string | undefined;
  children: ReactNode;
}) {
  const sound = useNotificationSound(sellerId, storeId);
  const feed = useNotifications(sellerId, storeId, sound.notifySound);
  const value = { ...feed, ...sound };
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider",
    );
  }
  return ctx;
}
